import React from 'react';
import { cva } from "class-variance-authority";
import { cn } from "../../utility/utils";

const toggleButtonVariants = cva(
  "relative inline-flex items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary cursor-pointer",
  {
    variants: {
      size: {
        sm: "w-9 h-5",
        default: "w-11 h-6",
        lg: "w-14 h-7",
      },
    },
    defaultVariants: {
      size: "default"
    }
  }
);

const bgColors = {
  default: {
    unchecked: "bg-gray-300",
    checked: "bg-primary"
  },
  success: {
    unchecked: "bg-gray-300",
    checked: "bg-color15"  // Green color
  },
  danger: {
    unchecked: "bg-gray-300",
    checked: "bg-color13"  // Red color
  },
  warning: {
    unchecked: "bg-gray-300",
    checked: "bg-color16"  // Yellow color
  }
};

const ToggleButton = ({
  isChecked = false,
  onChange = () => {},
  variant = "default",
  size = "default",
  className,
  disabled = false,
  ...props
}) => {
  const handleToggle = () => {
    if (disabled) return;
    onChange(!isChecked);
  };

  const knobSizeClasses = {
    sm: "h-3.5 w-3.5",
    default: "h-5 w-5", 
    lg: "h-6 w-6"
  };
  
  const knobTranslateClasses = {
    sm: isChecked ? "translate-x-4" : "translate-x-0.5",
    default: isChecked ? "translate-x-5" : "translate-x-0.5",
    lg: isChecked ? "translate-x-7" : "translate-x-0.5"
  };

  // Determine background color based on checked state and variant
  const bgColorClass = isChecked 
    ? bgColors[variant]?.checked || bgColors.default.checked 
    : bgColors[variant]?.unchecked || bgColors.default.unchecked;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isChecked}
      data-state={isChecked ? "checked" : "unchecked"}
      onClick={handleToggle}
      className={cn(
        toggleButtonVariants({ size }),
        bgColorClass, 
        className,
        disabled && "opacity-50 cursor-not-allowed"
      )}
      disabled={disabled}
      {...props}
    >
      <span 
        className={cn(
          "pointer-events-none block rounded-full bg-white shadow-md ring-0 transition-transform duration-200 ease-in-out",
          knobSizeClasses[size],
          knobTranslateClasses[size],
          disabled && "opacity-50"
        )}
      />
    </button>
  );
};

export default ToggleButton;
